<template>
  <v-input v-bind="[$attrs, $props]" v-on="$listeners" height="100%" width="100%">
    <!-- pass through scoped slots -->
    <template
      v-for="(_, scopedSlotName) in $scopedSlots"
      v-slot:[scopedSlotName]="slotData"
    >
      <slot :name="scopedSlotName" v-bind="slotData"></slot>
    </template>
    <!-- pass through normal slots -->
    <template v-for="(_, slotName) in $slots" v-slot:[slotName]>
      <slot :name="slotName"></slot>
    </template>

    <v-card flat tile>
      <div ref="canvasWrapper" class="signature-pad">
        <v-sheet outlined>
          <canvas :class="{ 'canvas-cursor': active }" ref="signatureCanvas" :width="canvasWidth" :height="canvasHeight"></canvas>
        </v-sheet>
        <div class="sign" v-if="!active && signature === undefined">
          <v-btn text @click="active = true"> {{ activateLabel }}</v-btn>
        </div>
      </div>
      <v-card-actions>
        <v-btn
          v-if="active"
          icon
          @click="clearSignature"
          :disabled="signaturePad && signaturePad.isEmpty()"
          ><v-icon>{{ clearSignatureIcon }}</v-icon></v-btn
        >
        <v-spacer></v-spacer>
        <v-btn icon v-if="active" @click="cancelSignature">
          <v-icon>mdi-close</v-icon>
        </v-btn>
        <v-btn v-if="active" icon @click="acceptSignature"
          ><v-icon>{{ acceptSignatureIcon }}</v-icon></v-btn
        >
        <v-btn v-else-if="signature" icon @click="active = true"
          ><v-icon>{{ reenterSignatureIcon }}</v-icon></v-btn
        >
      </v-card-actions>
    </v-card>
  </v-input>
</template>

<script lang="ts">
import { ref, onMounted, PropType, watch, computed } from 'vue';

import SignaturePad, { Options } from 'signature_pad';
import {
  VBtn,
  VCard,
  VInput,
  VSpacer,
  VCardActions,
  VCardTitle,
  VCardText,
  VIcon,
  VSheet,
} from 'vuetify/lib';

export default {
  emits: ['input'],
  inheritAttrs: false,
  extends: VInput,
  props: {
    activateLabel: {
      type: String,
      default: 'Tap To Sign',
    },
    reenterSignatureIcon: {
      type: String,
      default: 'mdi-signature-freehand',
    },
    acceptSignatureIcon: {
      type: String,
      default: 'mdi-check',
    },
    clearSignatureIcon: {
      type: String,
      default: 'mdi-eraser',
    },
    options: {
      type: Object as PropType<Options>,
    },
  },
  components: {
    VBtn,
    VCard,
    VInput,
    VSpacer,
    VCardActions,
    VCardTitle,
    VCardText,
    VIcon,
    VSheet,
  },
  setup(props: any, context: any) {
    const signatureCanvas = ref<HTMLCanvasElement>();
    const canvasWrapper = ref<HTMLDivElement>();
    const active = ref(false);
    const signaturePad = ref<SignaturePad | null>(null);

    const signature = ref(props.value);

    const canvasWidth = computed(() => props.width ?? '300px');
    const canvasHeight = computed(() => props.height ?? '150px');

    onMounted(() => {
      signaturePad.value = new SignaturePad(
        signatureCanvas.value as HTMLCanvasElement,
        props.options
      );
      if (signature.value) {
        const canvas = signatureCanvas.value as HTMLCanvasElement;
        signaturePad.value.fromDataURL(signature.value, {
          width: canvas.width,
          height: canvas.height,
        });
      }
      signaturePad.value.off();
    });

    watch(active, (value) => {
      value ? signaturePad.value?.on() : signaturePad.value?.off();
    });

    function acceptSignature(): void {
      if (signaturePad.value) {
        signature.value = signaturePad.value.isEmpty()
          ? undefined
          : signaturePad.value.toDataURL();
        context.emit('input', signature.value);
      }
      active.value = false;
    }

    function clearSignature(): void {
      if (signaturePad.value) {
        signaturePad.value.clear();
      }
    }

    function cancelSignature(): void {
      if (signaturePad.value) {
        signaturePad.value.clear();
        if (signature.value) {
          const canvas = signatureCanvas.value as HTMLCanvasElement;
          signaturePad.value.fromDataURL(signature.value, {
            width: canvas.width,
            height: canvas.height,
          });
        }
      }
      active.value = false;
    }


    return {
      signaturePad,
      active,
      signature,
      signatureCanvas,
      canvasWrapper,
      clearSignature,
      acceptSignature,
      cancelSignature,
      canvasWidth,
      canvasHeight
    };
  },
};
</script>

<style scoped lang="scss">
::v-deep .v-input__slot {
  flex-direction: column;
  align-items: flex-start;
}

.signature-pad {
  width: 100%;
  height: 100%;
  position: relative;
  display: inline-block;
}

.canvas-cursor {
  cursor: url('data:image/x-icon;base64,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')
      0 23,
    auto;
}

.sign {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}
</style>
